'use client';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { Button } from './button';
import { Card, CardContent, CardFooter } from './card';

// Function to delete a cookie
const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Function to get all cookies
const getAllCookies = (): string[] => {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name): name is string => name !== undefined);
};

export function CookieBanner() {
  const t = useTranslations('cookies.banner');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasConsent = localStorage.getItem('cookie-consent');
    if (!hasConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'true');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'false');
    // Remove all non-essential cookies
    const allCookies = getAllCookies();
    allCookies.forEach((cookie) => {
      if (!cookie.startsWith('_um_') && !cookie.startsWith('_ga')) {
        deleteCookie(cookie);
      }
    });
    // Remove Google Analytics script if it exists
    const gaScript = document.querySelector(
      'script[src*="google-analytics"], script[src*="gtag"]'
    );
    if (gaScript) {
      gaScript.remove();
    }
    // Remove GA cookies
    deleteCookie('_ga');
    deleteCookie('_gid');
    deleteCookie('_gat');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Card className='fixed bottom-4 left-4 right-4 sm:left-auto z-50 max-w-lg mx-auto shadow-lg gap-4'>
      <CardContent>
        <div className='flex justify-between items-start'>
          <p className='font-semibold'>{t('title')}</p>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 absolute top-2 right-2'
            onClick={handleAccept}
            aria-label='Close cookie banner'
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
        <p className='mt-1 text-xs text-muted-foreground'>{t('description')}</p>
      </CardContent>
      <CardFooter className='flex justify-end gap-2'>
        <Button variant='outline' size='sm' onClick={handleDecline}>
          {t('decline')}
        </Button>
        <Button size='sm' onClick={handleAccept}>
          {t('accept')}
        </Button>
      </CardFooter>
    </Card>
  );
}
